import { Component, type ErrorInfo } from 'react'

import { reloadApp } from '~/shared/platform'
import { Button } from '~/shared/ui/Button'
import { ErrorState } from '~/shared/ui/ErrorState'

import type { ErrorBoundaryProps, ErrorBoundaryState } from './types'

import styles from './ErrorBoundary.module.scss'

/**
 * Классовый error boundary — только классовые компоненты умеют ловить ошибки рендера
 * поддерева (React пока не даёт хук-эквивалента). Дефолтный фолбэк — ErrorState голосом Луни
 * с кнопкой «Обновить».
 *
 * Кнопка ПЕРЕЗАГРУЖАЕТ приложение, а не сбрасывает бордер (правка основателя). Раньше она
 * звала `reset`, то есть просила React перерисовать то же поддерево с теми же данными и тем
 * же состоянием. Помогало это только при случайном сбое, а самый частый случай — упавшие
 * данные или состояние: поддерево падало мгновенно и снова, и человек жал кнопку без всякого
 * результата. Перезагрузка поднимает приложение с нуля.
 *
 * `reset` при этом никуда не делся и по-прежнему уезжает в `fallback`-функцию: у своего
 * фолбэка может быть причина хотеть мягкий сброс, и решать это ему, а не нам.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, { componentStack: info.componentStack ?? '' })
  }

  reset = () => {
    this.setState({ error: null })
  }

  override render() {
    const { error } = this.state
    const { children, fallback } = this.props

    if (!error) return children

    if (typeof fallback === 'function') return fallback(error, this.reset)
    if (fallback) return fallback

    return (
      <div className={styles.root}>
        <ErrorState
          title='Звёзды грузятся. Буквально минуту.'
          action={
            <Button
              text='Обновить'
              variant='SECONDARY'
              size='S'
              width='FIT-CONTENT'
              onPress={reloadApp}
            />
          }
        />
      </div>
    )
  }
}
